import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Users, DollarSign, BarChart3, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  KpiCard, SectionHeader, SwissCard, SwissTable, SwissTH, SwissTD, Overline,
} from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const OPS = `${API}/api/command-center/ops`;
const PNL = `${API}/api/command-center/pnl`;

const CommandDashboardTab = () => {
  const [data, setData] = useState({ daily: null, members: [], pnl: null, settings: null, week: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const today = new Date();
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      dates.push(d.toISOString().split('T')[0]);
    }
    try {
      const [m, s, pnl, ...weekRes] = await Promise.all([
        fetch(`${OPS}/members`).then(r => r.json()),
        fetch(`${OPS}/settings`).then(r => r.json()),
        fetch(`${PNL}/7-pillar?year=${today.getFullYear()}&month=${today.getMonth() + 1}`).then(r => r.json()),
        ...dates.map(d => fetch(`${OPS}/daily-pl?date=${d}`).then(r => r.json())),
      ]);
      const todayPL = weekRes[weekRes.length - 1];
      const week = weekRes.map(w => ({ date: w.date, gross: w.gross, ace_keep: w.ace_keep, bookings: w.bookings_count }));
      setData({ daily: todayPL, members: m.members || [], pnl, settings: s, week });
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data.daily || !data.pnl) return <p className="text-sm text-[#666]">Loading Command Dashboard…</p>;

  const fmt = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
  const goal = data.settings?.revenue_goal_monthly || 10000;
  const monthRevenue = data.pnl.total_revenue;
  const goalPct = Math.min(100, (monthRevenue / goal) * 100);
  const maxWeekGross = Math.max(...data.week.map(d => d.gross || 0), 1);

  const fullAccess = data.members.filter(m => ['essentials', 'executive', 'black_card'].includes(m.tier));
  const totalBank = fullAccess.reduce((s, m) => s + (m.tier_meta?.hours_included || 0), 0);
  const usedBank = fullAccess.reduce((s, m) => s + (m.hours_used_this_period || 0), 0);
  const bankPct = totalBank > 0 ? (usedBank / totalBank) * 100 : 0;

  return (
    <div data-testid="command-dashboard-tab" className="space-y-5">
      <SectionHeader
        overline="♠ Morning Roll-Up"
        title="Command Dashboard"
      />

      {/* Top KPI strip — Swiss */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Today's Revenue" value={fmt(data.daily.gross)} icon={DollarSign} testid="kpi-today" />
        <KpiCard label="ACE Keep Today" value={fmt(data.daily.ace_keep)} icon={TrendingUp} tone="success" testid="kpi-ace-keep" />
        <KpiCard label="Month Revenue" value={fmt(monthRevenue)} icon={BarChart3} tone="danger" testid="kpi-month" />
        <KpiCard label="Active Members" value={data.members.length} icon={Users} testid="kpi-members" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 -mt-3">
        <p className="text-[10px] text-[#666] uppercase tracking-[0.15em]">{data.daily.bookings_count} bookings today</p>
        <p className="text-[10px] text-[#666] uppercase tracking-[0.15em]">After {data.settings.tax_reserve_pct}% tax reserve</p>
        <p className="text-[10px] text-[#666] uppercase tracking-[0.15em]">{goalPct.toFixed(0)}% of {fmt(goal)} goal</p>
        <p className="text-[10px] text-[#666] uppercase tracking-[0.15em]">{fullAccess.length} full-access</p>
      </div>

      {/* Goal progress bar */}
      <SwissCard className="p-5" testid="goal-bar">
        <div className="flex items-center justify-between mb-2">
          <Overline>Monthly Revenue Goal</Overline>
          <p className="text-xs"><span className="font-black">{fmt(monthRevenue)}</span> <span className="text-[#666]">/ {fmt(goal)}</span> · <span className="font-bold text-[#E52B20]">{goalPct.toFixed(1)}%</span></p>
        </div>
        <div className="h-2 bg-[#F9F9FA] border border-[#E5E5E5] overflow-hidden">
          <div className="h-full bg-[#E52B20] transition-all" style={{ width: `${goalPct}%` }} />
        </div>
      </SwissCard>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* 7-day chart */}
        <SwissCard className="p-5 lg:col-span-2" testid="week-chart">
          <Overline>Last 7 Days · Gross Revenue</Overline>
          <p className="text-[10px] text-[#666] mb-4">Daily roll-up · public + admin sources</p>
          <div className="flex items-end gap-2 h-32">
            {data.week.map((d, i) => {
              const h = (d.gross / maxWeekGross) * 100;
              const isToday = i === data.week.length - 1;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`week-bar-${i}`}>
                  <div className="w-full bg-[#F9F9FA] border border-[#E5E5E5] flex-1 flex items-end overflow-hidden">
                    <div className={`w-full ${isToday ? 'bg-[#E52B20]' : 'bg-[#0A0A0A]'} transition-all`} style={{ height: `${Math.max(2, h)}%` }} />
                  </div>
                  <p className={`text-[9px] font-mono ${isToday ? 'font-black text-[#E52B20]' : 'text-[#666]'}`}>{d.date.slice(5)}</p>
                  <p className="text-[10px] font-bold font-mono">{fmt(d.gross)}</p>
                </div>
              );
            })}
          </div>
        </SwissCard>

        {/* Member Hours Bank */}
        <SwissCard className="p-5" testid="hours-bank-widget">
          <Overline className="flex items-center gap-2 mb-3"><Clock className="h-3 w-3" /> Member Hours Banks</Overline>
          {fullAccess.length === 0 ? (
            <p className="text-xs text-[#666] py-6 text-center">No Full-Access members yet.</p>
          ) : (
            <>
              <div className="text-center mb-4">
                <p className="text-4xl font-black tracking-tight">{Math.round(usedBank)}<span className="text-[#666] text-base font-bold">/{totalBank}h</span></p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mt-1">Used this period</p>
                <div className="w-full h-2 bg-[#F9F9FA] border border-[#E5E5E5] mt-2 overflow-hidden">
                  <div className={`h-full ${bankPct >= 100 ? 'bg-[#E52B20]' : bankPct > 75 ? 'bg-[#F79009]' : 'bg-[#039855]'}`} style={{ width: `${Math.min(100, bankPct)}%` }} />
                </div>
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {fullAccess.slice(0, 5).map(m => (
                  <div key={m.member_id} className="flex justify-between text-xs">
                    <span className="font-bold truncate">{m.name}</span>
                    <span className="text-[#666] font-mono">{m.hours_used_this_period}/{m.tier_meta.hours_included}h</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </SwissCard>
      </div>

      {/* 7-Pillar P&L */}
      <SwissCard className="p-5" testid="seven-pillar-pnl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <Overline className="flex items-center gap-2"><BarChart3 className="h-3 w-3" /> 7-Pillar P&L · Month-to-Date</Overline>
          <div className="flex gap-3 text-[11px]">
            <span><span className="text-[#666]">Top Revenue:</span> <span className="font-bold">{data.pnl.highest_revenue_pillar || '—'}</span></span>
            <span><span className="text-[#666]">Top Margin:</span> <span className="font-bold">{data.pnl.highest_margin_pillar || '—'}</span></span>
          </div>
        </div>
        <SwissTable>
          <thead>
            <tr>
              <SwissTH>Pillar</SwissTH>
              <SwissTH className="text-right">Activity</SwissTH>
              <SwissTH className="text-right">Revenue</SwissTH>
              <SwissTH className="text-right">Costs</SwissTH>
              <SwissTH className="text-right">Net</SwissTH>
              <SwissTH className="text-right">Margin %</SwissTH>
            </tr>
          </thead>
          <tbody>
            {data.pnl.pillars.map(p => (
              <tr key={p.key} data-testid={`pillar-row-${p.key}`}>
                <SwissTD><span className="inline-flex items-center gap-2 font-bold"><span className="h-2 w-2" style={{ background: p.color }} />{p.label}</span></SwissTD>
                <SwissTD className="text-right text-[#666] font-mono">{p.count || 0}</SwissTD>
                <SwissTD className="text-right font-bold font-mono">{fmt(p.revenue)}</SwissTD>
                <SwissTD className="text-right text-[#666] font-mono">{fmt(p.costs)}</SwissTD>
                <SwissTD className={`text-right font-bold font-mono ${p.net > 0 ? 'text-[#039855]' : p.net < 0 ? 'text-[#E52B20]' : 'text-[#666]'}`}>{fmt(p.net)}</SwissTD>
                <SwissTD className="text-right text-[#666] font-mono">{p.margin_pct}%</SwissTD>
              </tr>
            ))}
            <tr className="bg-[#F9F9FA] font-bold">
              <SwissTD className="font-black">Total</SwissTD>
              <SwissTD></SwissTD>
              <SwissTD className="text-right font-mono">{fmt(data.pnl.total_revenue)}</SwissTD>
              <SwissTD className="text-right font-mono">{fmt(data.pnl.total_costs)}</SwissTD>
              <SwissTD className="text-right text-[#039855] font-mono">{fmt(data.pnl.total_net)}</SwissTD>
              <SwissTD className="text-right font-mono">{data.pnl.total_margin_pct}%</SwissTD>
            </tr>
          </tbody>
        </SwissTable>
        <p className="text-[10px] text-[#666] mt-3 italic">P2 Consulting · P3 Certification · P7 Media auto-populate when manual entries are added or events occur in their respective tables.</p>
      </SwissCard>
    </div>
  );
};

export default CommandDashboardTab;
